/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps */
/**
 * @typedef {import('../types/chunk').Chunk} Chunk
 * @typedef {import('../types/token').Token} Token
 * @typedef {import('../types/embedding').Embedding} Embedding
 * @typedef {import('../types/pca').PCA} PCA
 */

/**
 * ChunkList displays a list of chunk objects, each representing a chunk of text and its metadata.
 * 
 * @param {{
 *   chunks: Chunk[],
 *   tokens: Array,
 *   chunkList_tokenColorMode: string,
 *   getTokenColor: function,
 *   getSequenceColor: function
 * }} props
 */
import React, { useState, useEffect } from 'react';
import { DisplayParamTokenColorMode } from '../constants';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Collapse from '@mui/material/Collapse';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Tooltip from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

export default function ChunkList({
  chunks = [],
  chunkList_tokenColorMode,
  getTokenColor,
  getSequenceColor,
}) {
  const [mainOpen, setMainOpen] = useState(true);
  const [openStates, setOpenStates] = useState(() => chunks.map(() => false));

  // Reset open states if chunk count changes
  useEffect(() => {
    setOpenStates(chunks.map(() => false));
  }, [chunks.length]);

  if (openStates.length !== chunks.length) {
    return null;
  }

  const getDisplayChunkNumber = (chunk, fallbackIndex) => {
    if (Number.isInteger(chunk.chunkIndex)) {
      return chunk.chunkIndex + 1;
    }
    if (typeof chunk.chunkID === 'number' && Number.isInteger(chunk.chunkID)) {
      return chunk.chunkID + 1;
    }
    return fallbackIndex + 1;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        maxHeight: '100%', // Ensure the component can scroll if content overflows
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 1,
          py: 0,
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
          backgroundColor: 'rgba(255,255,255, 0.75)',
          borderBottom: '1px solid #e0e0e0',
          minHeight: 0,
          alignItems: 'center',
          cursor: 'pointer',
          display: 'flex',
        }}
        onClick={() => setMainOpen((prev) => !prev)}
      >
        <ExpandMoreIcon
          sx={{
            transform: mainOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
            transition: 'transform 0.2s',
            mr: 1,
          }}
        />
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="subtitle1" fontWeight="bold">
            Chunks
          </Typography>
          <Tooltip title="Each chunk is a group of consecutive tokens generated using a sliding window. This list shows all chunks, their tokens, and metadata.">
            <InfoOutlinedIcon fontSize="small" color="action" sx={{ ml: 0.5 }} />
          </Tooltip>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 1, fontSize: '0.75em'}}>
            Count: <b>{chunks.length}</b>
          </Typography>
          {chunks.length > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ ml: 1, fontSize: '0.75em'}}>
              Chunk size: <b>{chunks[0].chunkWindowSize}</b> tokens
            </Typography>
          )}
        </Stack>
      </Box>
      <Collapse in={mainOpen} timeout={0}>
        <Box
          sx={{
            p: 0,
            maxHeight: '80vh', // adjust 48px if your header is taller/shorter
            overflowY: 'auto',
          }}
        >
          {chunks.map((chunk, i) => {
            // Determine color for the chunk indicator using chunk index for sequence mode
            let circleColor = '#000000';
            let circleLabelColor = '#000000ff';
            if (chunkList_tokenColorMode === DisplayParamTokenColorMode.TOKEN_ID && chunk.chunkTokens?.[0]) {
              circleColor = getTokenColor(chunk.chunkTokens[0].id);
            } else if (chunkList_tokenColorMode === DisplayParamTokenColorMode.SEQUENCE) {
              circleColor = getSequenceColor(i, chunks.length);
            } else if (chunkList_tokenColorMode === DisplayParamTokenColorMode.BLACK) {
              circleColor = '#000000';
              circleLabelColor = '#ffffff';
            } else if (chunkList_tokenColorMode === DisplayParamTokenColorMode.WHITE) {
              circleColor = '#ffffff';
              circleLabelColor = '#000000';
            }

            return (
              <Box
                key={chunk.chunkID ?? i}
                sx={{
                  mb: 0,
                  backgroundColor: 'grey.50',
                  borderRadius: 0,
                  boxShadow: 'none',
                  border: '1px solid #e0e0e0',
                  mt: 0.,
                }}
              >
                <Box
                  sx={{
                    minHeight: 16,
                    px: 1,
                    py: 0,
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                  }}
                  onClick={() =>
                    setOpenStates((prev) =>
                      prev.map((open, idx) => (idx === i ? !open : open))
                    )
                  }
                >
                  <ExpandMoreIcon
                    fontSize="small"
                    sx={{
                      transform: openStates[i] ? 'rotate(0deg)' : 'rotate(-90deg)',
                      transition: 'transform 0.2s',
                      mr: 1,
                    }}
                  />
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        backgroundColor: circleColor,
                        flexShrink: 0,
                        border: '1px solid #ddd',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 10,
                        color: circleLabelColor,
                        fontWeight: 'normal',
                        position: 'relative',
                      }}
                    >
                      {/* Centered index (1-based) */}
                      {getDisplayChunkNumber(chunk, i)}
                    </Box>
                    {(chunk.chunkTokens?.[0]?.text || chunk.chunkLabel) && (
                      <Typography
                        variant="body2"
                        color='#444'
                        sx={{ fontSize: 10, ml: 1 }}
                        title="Tokens exposed by this chunk"
                      >
                        [{chunk.chunkTokens?.[0]?.text || chunk.chunkLabel}]
                      </Typography>
                    )}
                  </Stack>
                </Box>
                <Collapse in={!!openStates[i]} timeout={0}>
                  <Box sx={{ pt: 0.0, pb: 0.5, px: 1 }}>
                    <Typography
                      variant="body2"
                      fontFamily="monospace"
                      fontSize={10}
                      mb={0.0}
                      sx={{ whiteSpace: 'pre-wrap', display: 'inline', p: 0 }}
                    >
                      {(chunk.chunkTokens ?? []).map((t, idx) => {
                        return (
                          <Box
                            key={idx}
                            component="span"
                            sx={{
                              color: 'black',
                              backgroundColor:
                                chunkList_tokenColorMode === DisplayParamTokenColorMode.TOKEN_ID
                                  ? getTokenColor(t.id)
                                  : 'transparent',
                              borderRadius: 0.0,
                              px: 0.2,
                              py: 0.2,
                              display: 'inline',
                              lineHeight: 0.5,
                            }}
                          >
                            {t.text}
                          </Box>
                        );
                      })}
                    </Typography>
                    {(chunk.chunkEmbeddingReduced?.fullEmbedding || chunk.chunkEmbedding?.fullEmbedding) && (() => {
                      const embedding = chunk.chunkEmbeddingReduced?.fullEmbedding || chunk.chunkEmbedding?.fullEmbedding || [];
                      const preview = embedding.slice(0, 3).map((v) => v.toFixed(3)).join(', ');
                      const suffix = embedding.length > 3 ? ', ...' : '';
                      const modeLabel = chunk.chunkEmbeddingReduced?.fullEmbedding ? 'reduced' : 'full';

                      return (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: 'block',
                            mt: 0.25,
                            fontSize: 9,
                            lineHeight: 1.2,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                          title={`Embedding (${modeLabel}, ${embedding.length} dimensions)`}
                        >
                          emb ({modeLabel}, {embedding.length}d): [{preview}{suffix}]
                        </Typography>
                      );
                    })()}
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Box>
      </Collapse>
      <Box
        sx={{
          px: 1,
          py: 0.5,
          backgroundColor: 'rgba(162, 162, 162, 0.75)',
          borderBottomLeftRadius: 8,
          borderBottomRightRadius: 8,
          borderTop: '1px solid #e0e0e0',
          textAlign: 'center',
          color: 'text.secondary',
          fontSize: '0.75em',
        }}
      >
      </Box>
    </Box>
  );
}
